import { Check } from 'lucide-react';
import type { Chapter } from './ChapterRow';

type ChapterState = 'settled' | 'live' | 'background' | 'queued';

interface Props {
  chapters: Chapter[];
  getState: (chapter: Chapter) => ChapterState;
  /** How far the running stage has got, 0 to 1, for its own segment only. */
  liveFill?: number;
}

function fillFor(state: ChapterState, liveFill: number): number {
  if (state === 'settled' || state === 'background') return 1;
  if (state === 'live') return Math.max(0.04, Math.min(1, liveFill));
  return 0;
}

/**
 * One segment per pass, each filling over its own stage rather than over the
 * whole run, so the leading edge always sits under the pass that is running.
 */
export default function ScanStageRail({ chapters, getState, liveFill = 0.5 }: Props) {
  return (
    <div className="ob-rail" data-testid="onboarding-scan-rail">
      <div className="ob-rail__stages">
        {chapters.map((chapter, index) => {
          const state = getState(chapter);
          return (
            <span
              key={chapter.key}
              data-testid={`onboarding-scan-stage-${chapter.key}`}
              data-state={state}
              className="ob-rail__stage text-foreground"
            >
              {state === 'settled' || state === 'background' ? (
                <Check strokeWidth={3} className="text-primary" aria-hidden="true" />
              ) : (
                <span className="tabular-nums">{index + 1}</span>
              )}
              {chapter.stageName ?? chapter.label}
            </span>
          );
        })}
      </div>

      <div className="ob-rail__track">
        {chapters.map(chapter => {
          const fill = fillFor(getState(chapter), liveFill);
          return (
            <span key={chapter.key} className="ob-rail__seg" data-reached={fill > 0}>
              <i data-testid={`onboarding-scan-rail-fill-${chapter.key}`} style={{ transform: `scaleX(${fill.toFixed(3)})` }} />
            </span>
          );
        })}
      </div>
    </div>
  );
}
